import { toMapFn } from "~/utils/reducers";
import { type FacetFilterDefinition, type ResultCount } from "~/GearboxSelectionAssistant/models";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Characteristics } from "../constants";

export interface UseSelectFilterLogicProps {
  definition: FacetFilterDefinition;
  resultCount?: ResultCount;
  noResultBehavior?: "hide" | "disable" | "none";

  value: string[];
  onChange: (value: string[]) => void;
  multiselect?: boolean;
}

export const useSelectFilterLogic = ({
  definition,
  resultCount,
  multiselect = false,
  noResultBehavior = "hide",
  value: extValue,
  onChange: extOnChange,
}: UseSelectFilterLogicProps) => {
  const { t } = useTranslation();
  let selected: Record<string, boolean>;
  let makeOnClick;
  if (multiselect) {
    const value = extValue;
    const onChange = extOnChange;
    selected = toMapFn(
      value as string[],
      (v) => v,
      () => true
    );
    makeOnClick = useCallback(
      (code: string) => () => {
        if (value.includes(code)) {
          onChange(value.filter((it) => it !== code));
        } else {
          onChange([...value, code]);
        }
      },
      [value, onChange]
    );
  } else {
    const value = extValue[0] ?? null;
    const onChange = extOnChange;
    selected = { [value as string]: true };
    makeOnClick = useCallback(
      (code: string) => () => {
        if (value === code) {
          onChange([]);
        } else {
          onChange([code]);
        }
      },
      [value, onChange]
    );
  }
  // TODO - Exception for product type - values need to be translated
  const translateValues = definition.code === Characteristics.PRODUCT_TYPE;
  let items = definition.values.map((itemDef) => {
    const count = resultCount?.[itemDef.code] ?? 0;
    const isSelected = selected[itemDef.code];
    return {
      ...itemDef,
      name: translateValues ? t(itemDef.name) : itemDef.name,
      count,
      selected: isSelected,
      disabled: noResultBehavior === "disable" && count === 0 && !isSelected,
      trackingCode: itemDef.code,
    };
  });
  if (noResultBehavior === "hide") {
    items = items.filter((item) => item.count > 0 || item.selected);
  }

  // TODO - Exception for gear ratio
  let name = definition.label ?? "";
  name = name.startsWith("facet.") ? t(name) : name;

  return {
    name,
    items,
    makeOnClick,
    trackingCode: definition.code,
  };
};
